export type DemoCameraTier = 'T0' | 'T1' | 'T2' | 'T3' | 'T4' | 'T5';

export type DemoCameraRole =
  | 'face-candidates'
  | 'object'
  | 'health-only'
  | 'anpr'
  | 'anomaly'
  | 'passthrough';

type DemoCameraAssignment =
  | { readonly tier: 'T4'; readonly role: 'face-candidates'; readonly detectorAssigned: true }
  | { readonly tier: 'T2'; readonly role: 'object'; readonly detectorAssigned: true }
  | { readonly tier: 'T1'; readonly role: 'health-only'; readonly detectorAssigned: false }
  | { readonly tier: 'T3'; readonly role: 'anpr'; readonly detectorAssigned: true }
  | { readonly tier: 'T5'; readonly role: 'anomaly'; readonly detectorAssigned: true }
  | { readonly tier: 'T0'; readonly role: 'passthrough'; readonly detectorAssigned: false };

export type DemoCameraRosterEntry = {
  readonly ordinal: number;
  readonly shortId: string;
  readonly stableId: string;
  readonly name: string;
} & DemoCameraAssignment;

/**
 * Canonical assignment for the bounded 16-camera browser demo.
 *
 * Both console fixtures and the API mocks resolve camera ids through this
 * table. The stable UUID and short `cam-NN` id are aliases for the same row.
 */
export const DEMO_CAMERA_ROSTER = [
  {
    ordinal: 1,
    shortId: 'cam-01',
    stableId: '66666666-6666-6666-6666-666666666601',
    name: 'Lobby Entrance',
    tier: 'T4',
    role: 'face-candidates',
    detectorAssigned: true,
  },
  {
    ordinal: 2,
    shortId: 'cam-02',
    stableId: '66666666-6666-6666-6666-666666666602',
    name: 'Loading Dock',
    tier: 'T4',
    role: 'face-candidates',
    detectorAssigned: true,
  },
  {
    ordinal: 3,
    shortId: 'cam-03',
    stableId: '66666666-6666-6666-6666-666666666603',
    name: 'Pool Deck',
    tier: 'T4',
    role: 'face-candidates',
    detectorAssigned: true,
  },
  {
    ordinal: 4,
    shortId: 'cam-04',
    stableId: '66666666-6666-6666-6666-666666666604',
    name: 'Garage Entry',
    tier: 'T3',
    role: 'anpr',
    detectorAssigned: true,
  },
  {
    ordinal: 5,
    shortId: 'cam-05',
    stableId: '66666666-6666-6666-6666-666666666605',
    name: 'Roof Helipad',
    tier: 'T1',
    role: 'health-only',
    detectorAssigned: false,
  },
  {
    ordinal: 6,
    shortId: 'cam-06',
    stableId: '66666666-6666-6666-6666-666666666606',
    name: 'Mezzanine 3F',
    tier: 'T5',
    role: 'anomaly',
    detectorAssigned: true,
  },
  {
    ordinal: 7,
    shortId: 'cam-07',
    stableId: '66666666-6666-6666-6666-666666666607',
    name: 'Plaza Front Door',
    tier: 'T0',
    role: 'passthrough',
    detectorAssigned: false,
  },
  {
    ordinal: 8,
    shortId: 'cam-08',
    stableId: '66666666-6666-6666-6666-666666666608',
    name: 'Main Hall',
    tier: 'T0',
    role: 'passthrough',
    detectorAssigned: false,
  },
  {
    ordinal: 9,
    shortId: 'cam-09',
    stableId: '66666666-6666-6666-6666-666666666609',
    name: 'Food Court',
    tier: 'T0',
    role: 'passthrough',
    detectorAssigned: false,
  },
  {
    ordinal: 10,
    shortId: 'cam-10',
    stableId: '66666666-6666-6666-6666-66666666660a',
    name: 'South Wing',
    tier: 'T0',
    role: 'passthrough',
    detectorAssigned: false,
  },
  {
    ordinal: 11,
    shortId: 'cam-11',
    stableId: '66666666-6666-6666-6666-66666666660b',
    name: 'ATM Corridor',
    tier: 'T0',
    role: 'passthrough',
    detectorAssigned: false,
  },
  {
    ordinal: 12,
    shortId: 'cam-12',
    stableId: '66666666-6666-6666-6666-66666666660c',
    name: 'Service Lift',
    tier: 'T0',
    role: 'passthrough',
    detectorAssigned: false,
  },
  {
    ordinal: 13,
    shortId: 'cam-13',
    stableId: '66666666-6666-6666-6666-66666666660d',
    name: 'Plaza North',
    tier: 'T0',
    role: 'passthrough',
    detectorAssigned: false,
  },
  {
    ordinal: 14,
    shortId: 'cam-14',
    stableId: '66666666-6666-6666-6666-66666666660e',
    name: 'Yard Gate',
    tier: 'T0',
    role: 'passthrough',
    detectorAssigned: false,
  },
  {
    ordinal: 15,
    shortId: 'cam-15',
    stableId: '66666666-6666-6666-6666-66666666660f',
    name: 'Loading Bay',
    tier: 'T0',
    role: 'passthrough',
    detectorAssigned: false,
  },
  {
    ordinal: 16,
    shortId: 'cam-16',
    stableId: '66666666-6666-6666-6666-666666666610',
    name: 'Container Depot',
    tier: 'T0',
    role: 'passthrough',
    detectorAssigned: false,
  },
] as const satisfies ReadonlyArray<DemoCameraRosterEntry>;

const DEMO_CAMERA_BY_ID = new Map<string, DemoCameraRosterEntry>(
  DEMO_CAMERA_ROSTER.flatMap((camera) => [
    [camera.shortId, camera],
    [camera.stableId, camera],
  ]),
);

export function lookupDemoCamera(cameraId: string): DemoCameraRosterEntry | null {
  return DEMO_CAMERA_BY_ID.get(cameraId.trim().toLowerCase()) ?? null;
}
